/**
 * The second live route (#26 / R6.1) — a parent creates a household.
 *
 * `app/src/pages/Signup.jsx` is presentational by contract, exactly like `Login.jsx`: it performs
 * no fetch and no navigation, owns its own drafts, and calls
 * `onSubmit({ familyName, slug, name, email, password, consented })`. This container supplies the
 * real behavior and nothing else, so the recovered surface keeps its design and its tests.
 *
 * **The 201 is not a session.** `server/auth/routes/signup.routes.js` wraps session creation in
 * its own `try/catch`, logs a failure, and still answers 201. `signUp` in `api/session.js` handles
 * that; this file exists to make sure the FAMILY is told the right thing about it, because the
 * three failures mean three different things:
 *
 *   - **Creation refused** (409 taken, 400 bad field): the server's own sentence. They fix it here.
 *   - **Created (or possibly created), no session**: their household EXISTS, or may. "Try again"
 *     would send them to create a second one under a slug their own first household now holds.
 *     So the copy says created, points at sign-in, and the family STAYS on this surface with
 *     everything they typed still in the fields.
 *   - **A fault**: never phrased as though the family typed something wrong.
 */
import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  UNSENT_NOTICE_TTL_MS,
  clearSignupDraft,
  clearUnsentNotice,
  readSignupDraft,
  readUnsentNotice,
  writeSignupDraft,
  writeUnsentNotice,
} from './signupDraft.js';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from '../components/Button';
import LanternLoader from '../components/LanternLoader.jsx';
import ResendVerificationAction from '../components/ResendVerificationAction.jsx';
import { ApiError, api, apiUrl } from '../api/client.js';
import { signUp } from '../api/session.js';
import { useActor } from '../state/ActorContext.jsx';

const Signup = lazy(() => import('../pages/Signup.jsx'));

/**
 * Where the server keeps the Google profile it has already verified.
 *
 * `google.routes.js:254` serves it and returns 404 when the session holds none. It does NOT clear
 * it — `signup.routes.js:58` does that when the family is actually created.
 */
const PENDING_SIGNUP_PATH = '/api/auth/google/pending-signup';

/** Where a family lands once the household exists and the server agrees they are signed in. */
export const ONBOARDING_ROUTE = '/onboarding';

/** Where a family goes when the household exists but the session does not. */
export const SIGN_IN_ROUTE = '/login';

const FAULT_MESSAGE = 'We could not create your family just now — please try again in a moment.';

/** Appended whenever the household already exists, so the family never creates a second one. */
export const HOUSEHOLD_EXISTS_HINT = 'Your family is saved, so please sign in rather than starting over.';

/**
 * Turn a thrown error into one sentence the family can act on.
 *
 * `householdCreated` wins over the status code, because it changes what they should DO.
 */
export function signupMessage(error) {
  if (!(error instanceof ApiError)) return FAULT_MESSAGE;

  // Both household states come first, because they change what the family should DO. Neither may
  // ever read as "try again" — a second attempt collides with their own first household.
  if (error.householdCreated || error.householdMayExist) {
    return `${error.message} ${HOUSEHOLD_EXISTS_HINT}`;
  }
  // Status 0 is this client's own pre-flight refusal — a missing field, or unaccepted terms. Its
  // message was written for the family, so it is shown as-is.
  if (error.status === 0) return error.message;

  // 400, 409 and 422 are the server's judgements about what was submitted. Those are the
  // sentences that let a family fix the form — but `error` alone is often a bare label. The
  // middleware raises `new Error('Validation error')` and puts the useful content in `details`,
  // and a taken web address arrives with `suggestions`. Dropping either leaves the family
  // reading "Validation error" with no field marked and no way forward.
  if (error.status === 400 || error.status === 409 || error.status === 422) {
    return serverSentence(error.body) || FAULT_MESSAGE;
  }
  return FAULT_MESSAGE;
}

/**
 * Build one sentence out of everything the server actually said.
 *
 * The error shape is `{ error, type, statusCode }` plus an optional `details` (validation) or
 * `suggestions` (a taken web address) — `server/middleware/errorHandler.js`.
 */
function serverSentence(body) {
  const head = body?.error || body?.message;
  const headline = typeof head === 'string' && head.trim() ? head.trim() : '';

  // `details` is NOT a list of strings. `server/middleware/validate.js:26-29` builds
  // `[{ path, message }]` from the Zod issues. The first repair of this function filtered on
  // `typeof d === 'string'`, which discarded every real entry — and the test mocked strings, so
  // it went green while a real Zod 400 still rendered the bare label "Validation error".
  // A vacuous test on the repair itself, caught by the bounded recheck.
  //
  // Both shapes are accepted, because `errorHandler.js` is not the only producer of `details`.
  const details = Array.isArray(body?.details)
    ? body.details
        .map((d) => {
          if (typeof d === 'string') return d.trim();
          if (d && typeof d === 'object' && typeof d.message === 'string') return d.message.trim();
          return '';
        })
        .filter(Boolean)
    : [];
  const suggestions = Array.isArray(body?.suggestions)
    ? body.suggestions.filter((s) => typeof s === 'string' && s.trim())
    : [];

  const parts = [headline];
  if (details.length) parts.push(details.join(' '));
  if (suggestions.length) parts.push(`Free right now: ${suggestions.join(', ')}.`);
  return parts.filter(Boolean).join(' ');
}

export default function SignupRoute() {
  const { actor, setActor, status } = useActor();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // F-02: read ONCE, on mount. A draft restored mid-typing would fight the family for the caret.
  const [draft] = useState(readSignupDraft);

  // F-02: stop persisting the moment the household exists.
  //
  // WITHOUT THIS THE CLEAR DID NOT CLEAR, and the test caught it: `clearSignupDraft()` ran, then
  // the draft effect in `Signup.jsx` fired once more on the way out and wrote `{familyName: '',
  // slug: '', …}` straight back over it. A ref rather than state, because `persistDraft` has to
  // keep ONE identity for the lifetime of the form — a changing callback would re-run that effect
  // on every render.
  const signedUpRef = useRef(false);
  const persistDraft = useCallback((fields) => {
    if (signedUpRef.current) return;
    writeSignupDraft(fields);
  }, []);

  // F-02: THE WIZARD STEP LIVES HERE, not inside the form, and that is not a style choice.
  //
  // The `key` below deliberately REMOUNTS `Signup` when the Google profile lands, so the prefill
  // reaches fields that `useState(defaultX)` already read once. A remount also throws away
  // everything the form holds — and once the form owns four stops, that includes WHICH STOP the
  // family is standing on. A parent who typed their family name and pressed Continue while the
  // profile was still in flight would be silently thrown back to stop 1. Holding the step in this
  // component, which the remount does not touch, is what keeps the walk intact.
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // #638. The admin who just signed up is the one account that cannot sign in again unverified
  // (`auth.service.js`). When the first email did not go, the family hears it HERE, before
  // onboarding, while they are still signed in — not the next time they try the front door.
  const [unsent, setUnsent] = useState(null);
  // A restored warning carries only its owner identity and time, never a stored actor/session.
  const [restoredNotice, setRestoredNotice] = useState(readUnsentNotice);
  const warningRef = useRef(null);

  // THE FIRST-TIME GOOGLE USER'S HANDOFF (#543 fault A).
  //
  // `server/auth/routes/google.routes.js:191` parks the Google-verified profile in the session and
  // `:194` sends the browser to `/signup?google=1`. The OLD app met that at
  // `src/pages/FamilySignup.js:66`. This app had NOTHING — a grep for `google=1` across `app/src`
  // returned empty — so a first-time Google user was parked server-side and dropped into a page
  // that ignored them. They looped, which is what "i still cannot signing with google" looked like
  // from the outside.
  //
  // The flag arrives as a hash-route query because `serverReturnPath.js` translates the server's
  // path into one this HashRouter can read; without that step this effect never fires at all.
  const fromGoogle = searchParams.get('google') === '1';
  const [googleProfile, setGoogleProfile] = useState(null);
  const [googleError, setGoogleError] = useState(null);

  useEffect(() => {
    if (!fromGoogle) return undefined;
    let live = true;

    // The profile is read from the SERVER's session, never from the URL. It was verified by Google
    // and stored server-side precisely so it cannot be forged on the way back
    // (`google.routes.js:175-176` says so).
    api
      .get(PENDING_SIGNUP_PATH)
      .then((profile) => {
        if (live) setGoogleProfile(profile);
      })
      .catch((error) => {
        if (!live) return;
        // A 404 means the session has no parked profile: it expired, or somebody opened this URL
        // directly. Say that plainly and point at the one thing that works, rather than leaving a
        // half-filled form that cannot be submitted.
        const expired = error instanceof ApiError && error.status === 404;
        setGoogleError(
          expired
            ? 'That Google sign-in has expired. Please start again from the sign-in page.'
            : 'We could not finish your Google sign-in just now — please try again in a moment.',
        );
      });

    return () => {
      live = false;
    };
  }, [fromGoogle]);

  const onSubmit = useCallback(
    async ({ familyName, slug, name, email, password, consented, timezone, currency }) => {
      setSubmitError(null);
      setSubmitting(true);
      try {
        const { actor, verificationEmailSent, emailVerified } = await signUp({
          // `signUp` refuses an empty password before the network, which made the Google flow
          // impassable rather than merely awkward. The server takes its Google branch off the
          // session profile and never reads one.
          fromGoogle,
          familyName,
          slug,
          // The parent's own name. The server takes it as `displayName` and derives a username.
          displayName: name,
          email,
          password,
          // The Settings stop. Both are optional to the server, which falls back to
          // America/Edmonton and USD (`server/services/signup.service.js:188-189`).
          timezone,
          currency,
          tosAccepted: consented,
        });
        // F-02: the household exists now, so the draft has done its job. The flag goes up FIRST,
        // so the form's own effect cannot write an empty draft back over the clear on its way out.
        // It is cleared ONLY here, on success. Every failure path deliberately keeps it — the
        // error comment below says the family keeps everything they typed, and a draft that
        // vanished on a 409 would break that promise on the one path where retyping hurts most.
        signedUpRef.current = true;
        clearSignupDraft();
        // #638. A Google account arrives verified and is sent nothing, so only an UNVERIFIED
        // account with no email sent is warned.
        //
        // The actor is HELD until they continue, not set now. A new actor remounts everything
        // under `ActorProvider` (`FeedbackPreferences.jsx` keys its session on the identity), so
        // setting it here wiped this warning the moment it appeared — measured, the form came
        // back on stop 1. The server session already exists; only the handover waits.
        if (!verificationEmailSent && !emailVerified) {
          writeUnsentNotice(email, actor);
          setUnsent({ email, actor });
          return;
        }
        setActor(actor);
        navigate(ONBOARDING_ROUTE, { replace: true });
      } catch (error) {
        // The message is PAINTED here, on the surface the family is looking at.
        //
        // It used to also `navigate` to the front door carrying the sentence in router state.
        // Both halves were dead. React batches the `setSubmitError` and the `navigate` into one
        // render, so the error never painted before this route unmounted — and `LoginRoute` has
        // no `useLocation` and never read the state, so the sentence went nowhere. The family
        // was silently teleported to a bare sign-in page on exactly the path this change exists
        // to handle. Found by qwen3.8-max on the frozen candidate; both halves confirmed.
        //
        // Staying put is also the better answer. The family keeps everything they typed, and the
        // message tells them their household is saved and points at sign-in.
        setSubmitError(signupMessage(error));
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, setActor, fromGoogle],
  );

  // Fresh signup has its own server receipt. Restored storage must first match a known actor.
  const sameOwner = actor?.kind === 'parent'
    && String(actor?.id) === restoredNotice?.userId
    && String(actor?.tenantId) === restoredNotice?.tenantId;
  const restoredInvalid = fromGoogle || status === 'unauthenticated'
    || (status === 'authenticated' && (!sameOwner || actor?.emailVerified === true));
  const restoreAllowed = status === 'authenticated' && !restoredInvalid;
  const notice = unsent || (restoredNotice && restoreAllowed ? { email: restoredNotice.email } : null);
  useEffect(() => {
    if (!restoredNotice || unsent) return undefined;
    const forget = () => { clearUnsentNotice(); setRestoredNotice(null); };
    if (restoredInvalid) {
      forget();
      return undefined;
    }
    const delay = Math.max(0, restoredNotice.createdAt + UNSENT_NOTICE_TTL_MS - Date.now());
    const timer = setTimeout(forget, delay);
    return () => clearTimeout(timer);
  }, [restoredNotice, unsent, restoredInvalid]);

  // #638 L2. The warning replaces the whole form, so the control that held focus is gone and focus
  // is on <body>. A live region inserted whole is not reliably announced, so the warning takes
  // focus, which a screen reader does follow. Only lost focus is moved.
  //
  // A LAYOUT effect, so focus moves inside the commit that draws the warning. As a passive effect
  // it moved later, and anything reading focus in between found <body> (measured 2026-09-30).
  const showingNotice = notice !== null;
  useLayoutEffect(() => {
    if (!showingNotice) return;
    const active = document.activeElement;
    if ((active == null || active === document.body) && warningRef.current) warningRef.current.focus();
  }, [showingNotice]);

  if (notice) {
    return (
      <main style={{ padding: '2rem', maxWidth: '34rem' }} data-component="VerificationEmailUnsent" data-theme="light" data-register="ledger">
        <h1>Your family is ready</h1>
        <p role="status" ref={warningRef} tabIndex={-1}>
          {unsent
            ? 'We could not send the email that confirms your address. You are signed in now, but you will need that email to sign in again later.'
            : 'At signup, we could not send the email that confirms your address. You are signed in now. If you have not already verified your address, request a link before you need to sign in again.'}
        </p>
        <ResendVerificationAction email={notice.email} />
        <p>
          <Button
            variant="primary"
            onClick={() => {
              // Forgotten FIRST: `setActor` remounts this route, and a remount reads the notice.
              clearUnsentNotice();
              if (notice.actor) setActor(notice.actor);
              navigate(ONBOARDING_ROUTE, { replace: true });
            }}
          >
            Continue to set up your family
          </Button>
        </p>
      </main>
    );
  }

  return (
    <Suspense
      fallback={
        <div style={{ padding: '2rem' }}>
          <LanternLoader label="Lighting the lanterns — loading the signup page." />
        </div>
      }
    >
      <Signup
        // `Signup.jsx` seeds every draft with `useState(defaultX)`, which reads its argument on
        // the FIRST render only, and that file has no `useEffect` to re-sync. `googleProfile`
        // arrives asynchronously from `api.get(PENDING_SIGNUP_PATH)` above, so whenever this form
        // mounts before the fetch resolves — the normal ordering, since the lazy chunk is usually
        // cached while the fetch is a real round trip — the fields stayed empty forever and the
        // prefill silently did not happen. Remounting on the profile's identity is smaller and
        // safer than a syncing effect, which would clobber whatever the family already typed.
        // Independent review finding H1, `docs/reviews/2026-09-13-google-signup-handoff-qwen.md`.
        key={googleProfile?.googleId ?? 'no-google-profile'}
        currentStep={step}
        onStepChange={setStep}
        onSubmit={onSubmit}
        submitting={submitting}
        submitError={submitError || googleError}
        fromGoogle={fromGoogle}
        // F-02 seeds. A restored draft WINS over the Google prefill wherever it has a value,
        // because a draft is something the family typed and a prefill is a suggestion. Where the
        // draft is empty the Google value still fills the field, so neither feature loses.
        defaultEmail={draft.email || googleProfile?.email || ''}
        defaultName={draft.name
          || [googleProfile?.firstName, googleProfile?.lastName].filter(Boolean).join(' ')}
        defaultFamilyName={draft.familyName || googleProfile?.familyName || ''}
        defaultSlug={draft.slug || googleProfile?.slug || ''}
        defaultConsented={!!draft.consented}
        {...(draft.timezone ? { defaultTimezone: draft.timezone } : {})}
        {...(draft.currency ? { defaultCurrency: draft.currency } : {})}
        // A stable callback, never an inline arrow: see the effect in `Signup.jsx`.
        onDraftChange={persistDraft}
        // `Signup.jsx:929` renders "Continue with Google instead" unconditionally, and
        // `Signup.jsx:821` calls `onGoogle` only when that prop is a function. Without it the
        // screen offered an enabled, focusable control that did nothing — clicked live on
        // `#/signup` 2026-09-13, the URL never moved and no request was made. `LoginRoute` was
        // repaired for this same defect class on 2026-09-10; this screen was missed.
        //
        // A NAVIGATION, not a fetch: Google sign-in is a server-side redirect flow (#346) mounted
        // at `/api/auth/google` (`server/index.js:156`). `fetch` cannot follow a redirect into the
        // address bar, so the browser has to go there itself.
        onGoogle={() => window.location.assign(apiUrl('/api/auth/google'))}
      />
    </Suspense>
  );
}
